import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import dayjs from 'dayjs';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import {
  TRIAL_ENABLED,
  useTrialStatusQuery,
} from '@cast/shared-trial-data-access';
import { TrialStatusEnum } from '@cast/types-organization-management';

import { _CASTAI_TRIAL } from './trialStatus';
import { CastTrialState, TrialState } from './types';
import { getTrialAnalytics } from './utils/get-trial-analytics';
import {
  getExpiresInDays,
  getTrialState,
  isEligibleForTrial,
} from './utils/get-trial-state';

const trialContextDefault: CastTrialState = {
  isLoading: false,
  hasError: false,
  isEligible: false,
  hasContactedSales: false,
  isAboutToExpire: false,
  state: 'post-trial',
  isPreTrial: false,
  isExpired: false,
  expiresInDays: 0,
  expiresInDaysGracePeriod: undefined,
};

export const TrialContext = createContext<CastTrialState>(trialContextDefault);

type DevOverrides = {
  useTrialStateOverride?: () => TrialState | undefined;
  useIsEligibleOverride?: () => boolean | undefined;
};

type TrialProviderProps = PropsWithChildren<{
  devOverrides?: DevOverrides;
}>;

const noOverride = () => undefined;

/**
 * How TRIAL works from BE side:
 *
 * 1) Trial starts on DAY 1
 * 2) From DAY 23 to DAY 30, BE will send notifications that trial will end soon.
 * 3) From DAY 31 to DAY 36, BE will send notifications that you should contact sales or lose access to paid features.
 * 4) On DAY 37 we send a notifications that the account is now in read-only and we switch the account to read-only.
 */
export const TrialProvider = ({
  children,
  devOverrides,
}: TrialProviderProps) => {
  const { data, isLoading, error } = useTrialStatusQuery({
    enabled: TRIAL_ENABLED,
  });

  const useTrialStateOverride =
    devOverrides?.useTrialStateOverride ?? noOverride;
  const useIsEligibleOverride =
    devOverrides?.useIsEligibleOverride ?? noOverride;

  const isTrialInGracePeriod = data?.inGracePeriod ?? false;
  const isTrialActive =
    !isTrialInGracePeriod && data?.status === TrialStatusEnum.ACTIVE;
  const hasContactedSales = data?.salesContacted ?? false;
  const trialEnd = data?.trialEndTime ? dayjs(data.trialEndTime) : null;
  const expiresInDays = getExpiresInDays(trialEnd);
  const isAboutToExpire =
    isTrialActive &&
    isFinite(expiresInDays) &&
    expiresInDays >= 0 &&
    expiresInDays <= 7;
  const gracePeriodEnd = data?.gracePeriodEndTime
    ? dayjs(data.gracePeriodEndTime)
    : null;
  const expiresInDaysGracePeriod = getExpiresInDays(gracePeriodEnd);
  const stateDevOverride = useTrialStateOverride();
  const eligibleDevOverride = useIsEligibleOverride();

  const computedState = getTrialState(data);
  const state: TrialState = stateDevOverride ?? computedState;
  const isEligible = eligibleDevOverride ?? isEligibleForTrial(data?.status);
  const isPreTrial = isEligible && state === 'pre-trial';
  const isExpired = data?.status === TrialStatusEnum.EXPIRED;

  useEffect(() => {
    _CASTAI_TRIAL.isEligible = isEligible;
    _CASTAI_TRIAL.state = state;
    _CASTAI_TRIAL.hasContactedSales = hasContactedSales;
    _CASTAI_TRIAL.expiresInDays = expiresInDays;
    _CASTAI_TRIAL.isAboutToExpire = isAboutToExpire;
  }, [isEligible, state, hasContactedSales, expiresInDays, isAboutToExpire]);

  const trialAnalytics = useMemo(
    () => (data ? getTrialAnalytics(data) : undefined),
    [data]
  );

  useSetAnalyticsGlobal('trialStatus', trialAnalytics?.trialStatus);
  useSetAnalyticsGlobal('isEligible', trialAnalytics?.isEligible);
  useSetAnalyticsGlobal('hasContactedSales', trialAnalytics?.hasContactedSales);

  const value = useMemo(
    () => ({
      isLoading,
      state,
      isExpired,
      expiresInDays,
      isAboutToExpire,
      isEligible,
      isPreTrial,
      hasContactedSales,
      expiresInDaysGracePeriod,
      hasError: !!error,
    }),
    [
      isLoading,
      state,
      isExpired,
      expiresInDays,
      isAboutToExpire,
      isEligible,
      isPreTrial,
      hasContactedSales,
      expiresInDaysGracePeriod,
      error,
    ]
  );

  return (
    <TrialContext.Provider value={value}>{children}</TrialContext.Provider>
  );
};

export const useTrial = () => {
  return useContext(TrialContext);
};
